import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Dropdown,
  Empty,
  List,
  Segmented,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  FiGrid,
  FiTarget,
  FiBell,
  FiUser,
  FiCreditCard,
  FiLayers,
  FiShield,
  FiMoreVertical,
  FiInbox,
  FiAlertCircle,
  FiInfo,
  FiCheckCircle,
  FiBellOff,
  FiTrash2,
  FiCalendar,
} from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import { useSelector } from "react-redux";
import MemberServiceInstance from "../../services/MemberService";
import { getLowerCaseString } from "../../helpers/StringFormatHelper";

const { Text } = Typography;

function Subnav() {
  const LINKS = [
    { to: "/dashboard", label: "Dashboard", Icon: FiGrid },
    { to: "/my/predictions", label: "My predictions", Icon: FiTarget },
    { to: "/notifications", label: "Notifications", Icon: FiBell },
    { to: "/account/profile", label: "My profile", Icon: FiUser },
    { to: "/account/billing", label: "Billing", Icon: FiCreditCard },
    { to: "/plans", label: "Plans & Pricing", Icon: FiLayers },
    { to: "/account/security", label: "Change password", Icon: FiShield },
  ];
  const { pathname } = useLocation();
  const isActive = (to) => pathname === to || (to !== "/" && pathname.startsWith(to));

  return (
    <nav className="px-subnav" aria-label="Account navigation">
      <div className="px-subnav__inner">
        {LINKS.map(({ to, label, Icon }) => (
          <Link key={to} to={to} className={`px-subnav__link ${isActive(to) ? "is-active" : ""}`}>
            <Icon aria-hidden />
            <span>{label}</span>
          </Link>
        ))}
      </div>
    </nav>
  );
}

const typeMeta = {
  security: { color: "gold", icon: <FiShield />, label: "Security" },
  system: { color: "processing", icon: <FiInfo />, label: "System" },
  billing: { color: "purple", icon: <FiCreditCard />, label: "Billing" },
  predictions: { color: "blue", icon: <FiTarget />, label: "Predictions" },
};

export default function Notifications() {
  const token = useSelector((state) => state.Auth.token);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = React.useState("all");
  const [selected, setSelected] = React.useState(new Set());
  const [unreadCount, setUnreadCount] = React.useState(0);

  const filtered = React.useMemo(() => {
    if (tab === "all") return items;
    if (tab === "unread") return items.filter((n) => n.isSeen === false);
    return items.filter((n) => n.notificationTypeCode === getLowerCaseString(tab));
  }, [items, tab]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await MemberServiceInstance.getNotifications(token, tab);
        const responseData = response.data;
        const { success, result } = responseData;
        if (success === true) {
          setItems(result?.items);
          setUnreadCount(result?.unseen);
        }
      } catch (error) {

      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [token]);

  const clearSelection = () => setSelected(new Set());
  const markSelectedRead = () => {
    setItems((prev) => prev.map((n) => (selected.has(n.id) ? { ...n, unread: false } : n)));
    clearSelection();
  };
  const deleteSelected = async (n) => {
    try {
      const response = await MemberServiceInstance.deleteNotification(token, n.uuid);
      const responseData = response.data;
      if (responseData) {
        setItems(items?.filter((item) => item?.id !== n?.id));
        if (n.isSeen === false) setUnreadCount(unreadCount - 1);
        clearSelection();
      }
    } catch (error) {

    }
  };
  const markAllRead = async () => {
    try {
      const response = await MemberServiceInstance.readAllNotifications(token, {});
      const responseData = response.data;
      if (responseData) {
        setItems((prev) => prev.map((n) => ({ ...n, isSeen: true })));
        setUnreadCount(0);
        clearSelection();
      }
    } catch (error) {

    }
  };

  const itemMenu = (n) => ({
    items: [
      { key: "read", label: n.isSeen === false ? "Mark as read" : "Mark as unread" },
      { type: "divider" },
      { key: "delete", danger: true, label: "Delete" },
    ],
    onClick: async ({ key }) => {
      if (key === "read") {
        try {
          const requestData = { isSeen: !n.isSeen };
          const response = await MemberServiceInstance.updateNotification(token, requestData, n.uuid);
          const responseData = response.data;
          if (responseData) {
            setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isSeen: !x.isSeen } : x)));
            requestData.isSeen === false ? setUnreadCount(unreadCount + 1) : setUnreadCount(unreadCount - 1);
            clearSelection();
          }
        } catch (error) {

        }
      }
      if (key === "delete") {
        deleteSelected(n)
      }
    },
  });

  const ui = {
    heroWrap: {
      position: "relative",
      padding: "80px 16px 80px",
      background:
        "radial-gradient(1200px 280px at 50% -30%, rgba(59,130,246,0.25), transparent 60%), linear-gradient(180deg, #0b1020 0%, #0c1428 100%)",
      color: "white",
      borderBottom: "1px solid rgba(255,255,255,.06)",
    },
    heroInner: { maxWidth: 1350, margin: "0 auto" },
    badge: {
      display: "inline-flex", alignItems: "center", gap: 8,
      fontSize: 12, color: "#93c5fd", background: "rgba(147,197,253,.10)",
      border: "1px solid rgba(147,197,253,.30)", padding: "6px 12px", borderRadius: 999,
    },
    heroTitle: { margin: "14px 0 10px", fontSize: 36, lineHeight: 1.15, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" },
    heroSub: { margin: 0, color: "rgba(255,255,255,.85)", maxWidth: 820 },
  };

  return (
    <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="Notifications">
      <section style={ui.heroWrap} aria-label="Hero">
        <div style={ui.heroInner}>
          <span style={ui.badge}><FiBell /> Notifications</span>
          <h1 style={ui.heroTitle}>Stay on top of updates</h1>
          <p style={ui.heroSub}>System alerts, billing messages, and prediction updates, all in one place.</p>
        </div>
      </section>

      <Subnav />

      <section className="px-notifs">
        <div className="px-wrap px-wrap--onecol">
          <main className="px-main">
            <Card className="px-card" bordered={false}>
              <div className="px-toolbar">
                <Space wrap size={[8, 8]}>
                  <Segmented
                    value={tab}
                    onChange={setTab}
                    options={[
                      { label: "All", value: "all", icon: <FiInbox /> },
                      { label: <span>Unread <Badge count={unreadCount} size="small" color="#004aad"></Badge></span>, value: "unread", icon: <FiAlertCircle /> },
                      { label: "System", value: "system", icon: <FiInfo /> },
                      { label: "Predictions", value: "predictions", icon: <FiTarget /> },
                    ]}
                  />
                </Space>

                <Space wrap>
                  {selected.size > 0 ? (
                    <>
                      <Button onClick={markSelectedRead} icon={<FiCheckCircle />}>Mark selected read</Button>
                      <Button danger onClick={() => deleteSelected(selected.uuid)} icon={<FiTrash2 />}>Delete selected</Button>
                      <Button type="text" onClick={clearSelection}>Clear selection</Button>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Mute non-critical">
                        <Button icon={<FiBellOff />} type="text" />
                      </Tooltip>
                      <Button onClick={markAllRead} icon={<FiCheckCircle />}>Mark all read</Button>
                    </>
                  )}
                </Space>
              </div>

              <List
                loading={loading}
                itemLayout="vertical"
                dataSource={filtered}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={<span>No notifications {tab !== "all" ? `in “${tab}”` : ""}.</span>}
                    />
                  ),
                }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "50"],
                  responsive: true,
                }}
                renderItem={(n) => {
                  const meta = typeMeta[n.notificationTypeCode] || typeMeta.system;

                  return (
                    <List.Item className="px-item" key={n.id}>
                      <div className={`px-item-card ${n.isSeen === false ? "is-unread" : ""}`} data-id={n.id}>
                        <div className="px-unread-bar" aria-hidden />

                        <div className="px-left">
                          <div className="px-iconwrap">{meta.icon}</div>
                        </div>

                        <div className="px-content">
                          <div className="px-toprow">
                            <div className="px-titlewrap">
                              <span className="px-title">{n.title}</span>
                              <Tag color={meta.color} className="px-tag">{meta.label}</Tag>
                            </div>
                            <div className="px-time">
                              <FiCalendar aria-hidden />
                              <Text type="secondary">{n?.notificationDate}, {n?.notificationTime}</Text>
                            </div>
                          </div>

                          <div className="px-body">
                            <Text>{n.description}</Text>
                            {n.href && <Link to={n.href} className="px-link">View details</Link>}
                          </div>
                        </div>

                        <div className="px-actions">
                          <Dropdown menu={itemMenu(n)} trigger={["click"]}>
                            <Button type="text" className="px-action-icon" icon={<FiMoreVertical />} />
                          </Dropdown>
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            </Card>
          </main>
        </div>

        <style>{`
          .px-notifs { background:#f7f7f9; padding:30px; }
          .px-wrap { max-width: 1350px; margin: 0 auto; }
          .px-wrap--onecol { display:block; }

          @media (max-width: 520px) { .px-notifs { padding:16px; } }

          .px-card {
            border-radius:18px;
            background:#fff;
            border:1px solid rgba(0,0,0,.06);
            box-shadow: 0 10px 30px rgba(0,0,0,.06);
          }

          .px-toolbar {
            display:flex; align-items:center; justify-content:space-between;
            gap:12px; margin-bottom:12px; flex-wrap:wrap;
          }

          /* Ticket-style item using grid areas for easy responsiveness */
          .px-item { padding: 0; border: none; }
          .px-item-card {
            position: relative;
            display: grid;
            grid-template-columns: auto 1fr auto;
            grid-template-areas: "left content actions";
            align-items: center;
            gap: 14px;
            padding: 14px;
            border-radius: 14px;
            border: 1px solid rgba(2,6,23,.06);
            background: #fff;
            box-shadow: 0 6px 16px rgba(2,6,23,.05);
            transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
          }
          .px-item-card:hover {
            transform: translateY(-1px);
            box-shadow: 0 12px 28px rgba(2,6,23,.08);
            border-color: rgba(2,6,23,.10);
          }
          .px-item-card.is-unread { background: linear-gradient(180deg, #fff, #fafafa); }
          .px-unread-bar {
            position:absolute; left:0; top:0; bottom:0; width:5px;
            border-top-left-radius:14px; border-bottom-left-radius:14px;
            background: transparent;
          }
          .px-item-card.is-unread .px-unread-bar { background: #1d4ed8; }

          .px-left { grid-area: left; display:flex; align-items:center; gap:10px; }
          .px-iconwrap {
            width:40px; height:40px; display:grid; place-items:center;
            border-radius:12px; flex: 0 0 40px;
            background:#eef2ff; color:#1e3a8a; border:1px solid rgba(30,58,138,.2);
          }
          .px-check { margin-left: 4px; }

          .px-content { grid-area: content; display:flex; flex-direction:column; gap:6px; min-width:0; }
          .px-toprow {
            display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap;
          }
          .px-titlewrap { display:flex; align-items:center; gap:10px; flex-wrap:wrap; min-width:0; }
          .px-title {
            font-weight:800; color:#0f172a; font-size:16px; line-height:1.25;
            word-break: break-word; overflow-wrap: anywhere;
          }
          .px-tag { border-radius:999px; }

          .px-time { display:flex; align-items:center; gap:8px; white-space:nowrap; }
          .px-time svg { opacity: .7; }

          .px-body { display:flex; align-items:center; gap:10px; flex-wrap:wrap; color:#334155; }
          .px-body .ant-typography { word-break: break-word; overflow-wrap: anywhere; }
          .px-link { font-weight:700; color:#1d4ed8; text-decoration:none; }

          .px-actions { grid-area: actions; display:flex; align-items:center; gap:4px; }
          .px-actions .ant-btn { min-height: 36px; }

          .px-action-btn { display:inline-flex; align-items:center; gap:8px; }
          .px-action-icon { padding-inline: 8px; }

          /* Mobile responsiveness */
          @media (max-width: 640px) {
            .px-item-card {
              grid-template-columns: auto 1fr;
              grid-template-areas:
                "left content"
                "left actions";
              gap: 10px;
              padding: 12px 10px;
            }
            .px-left {
            display: none;
              align-items: center; gap: 6px;
              flex-direction: column; /* icon above checkbox */
            }
            .px-iconwrap { width:36px; height:36px; border-radius:10px; }
            .px-check .ant-checkbox-inner { width: 16px; height: 16px; }

            .px-toprow { flex-direction: column; align-items: flex-start; gap: 6px; }
            .px-title { font-size: 15px; }
            .px-time { font-size: 12px; }
            .px-body { align-items: flex-start; }

            /* Show actions on their own row; use icon-first, hide text label to save space */
            .px-actions { justify-content: flex-end; gap: 6px; }
            .px-action-label { display: none; }
            .px-actions .ant-btn { min-height: 34px; }
          }

          .px-list-footer { margin-top:12px; display:flex; justify-content:flex-end; }

          /* Subnav (shared) */
          .px-subnav {
            position: sticky; top: 0; z-index: 20;
            backdrop-filter: blur(6px);
            background: rgba(255,255,255,.85);
            border-bottom: 1px solid rgba(2,6,23,.08);
          }
          .px-subnav__inner {
            max-width: 1400px; margin: 0 auto;
            padding: 10px 16px;
            display: flex; gap: 8px; align-items: center; flex-wrap: nowrap;
            overflow-x: auto; -webkit-overflow-scrolling: touch;
          }
          .px-subnav__link {
            display: inline-flex; align-items: center;
            padding: 8px 12px; gap: 8px;
            border-radius: 999px;
            font-size: 13px; font-weight: 700;
            color: #334155;
            border: 1px solid transparent;
            white-space: nowrap; text-decoration:none;
          }
          .px-subnav__link:hover { background: #f1f5f9; border-color: rgba(2,6,23,.08); }
          .px-subnav__link.is-active {
            color: #1e3a8a;
            background: rgba(37,99,235,.10);
            border-color: rgba(37,99,235,.25);
          }
        `}</style>
      </section>
    </Layout>
  );
}
